import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { useToast } from '@shared/ui';
import { postCartOrderGiveaway } from 'apis/cart';
import { type CartProductCardType } from 'app/components/ProductCard';
import i18n from '@shared/i18n';
import { useCartContext } from 'app/cart/context';
import { TRACK_EVENT, logEvent } from 'app/libs/track';
import { LaptopModal } from './LaptopModal';
import { MobileBottomSheet } from './MobileBottomSheet';

export interface OrderGiveawaySheetModalReturnRef {
    showOrderGiveaway: (params: {
        copy: PromotionalCopy;
        promos: PromoOrderGiveawayPromos[];
        selectedGiveaway: CartProductCardType | undefined;
    }) => void;
}

interface OrderGiveawaySheetModalPropTypes {}

export const OrderGiveawaySheetModal = forwardRef<
    OrderGiveawaySheetModalReturnRef,
    OrderGiveawaySheetModalPropTypes & { isMobile: boolean }
>((props, ref) => {
    const { isMobile } = props;

    const toast = useToast();

    const [visible, setVisible] = useState(false);

    const [promoThreshold, setPromoThreshold] = useState('');

    const [promoCopy, setPromoCopy] = useState<PromotionalCopy | null>(null);

    const [promos, setPromos] = useState<PromoOrderGiveawayPromos[]>([]);

    const [cartId, setCartId] = useState<string | null>(null);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const {
        actions: { calculate },
    } = useCartContext();

    const [selectedProduct, setSelectedProduct] = useState<{
        cart_id?: string;
        product_id: string;
        promo_id: string;
        add_on_limit: number;
        spec_details: BuyerPromoProductSpecDetail[];
    } | null>(null);

    const [selectedSpecs, setSelectedSpecs] =
        useState<BuyerPromoProductSpecDetail | null>(null);

    useImperativeHandle(ref, () => ({
        showOrderGiveaway: ({ copy, promos: _promos, selectedGiveaway }) => {
            setVisible((prev) => !prev);

            setPromoThreshold(String(copy.placeholder?.threshold.value || ''));

            setPromoCopy(copy);

            setPromos(_promos);

            if (selectedGiveaway) {
                const selectedPromo = _promos.find(
                    (promo) => promo.id === selectedGiveaway.promoId
                );

                if (selectedPromo) {
                    const _selectedProduct = selectedPromo.products.find((product) =>
                        selectedGiveaway.specInfo
                            ? product.spec_details.some(
                                  (spec) => spec.id === selectedGiveaway.specDetailId
                              )
                            : product.id === selectedGiveaway.productId
                    );

                    if (_selectedProduct) {
                        setCartId(selectedGiveaway.id);
                        setSelectedProduct({
                            product_id: _selectedProduct.id,
                            promo_id: _selectedProduct.promo_id,
                            add_on_limit: _selectedProduct.add_on_limit,
                            spec_details: selectedGiveaway.specInfo
                                ? _selectedProduct.spec_details
                                : [],
                        });

                        if (selectedGiveaway.specInfo) {
                            const selectedSpec = _selectedProduct.spec_details.find(
                                (spec) => spec.id === selectedGiveaway.specDetailId
                            );

                            if (selectedSpec) {
                                setSelectedSpecs(selectedSpec);
                            }
                        }
                    }
                }
            }
        },
    }));

    const handleSelectProduct = (product: BuyerPromoProduct) => {
        const { promo_id, id, add_on_limit, spec_details } = product;

        setSelectedProduct((prevSelected) => {
            if (prevSelected?.product_id === id && prevSelected?.promo_id === promo_id) {
                return null;
            }

            return {
                ...prevSelected,
                product_id: id,
                promo_id,
                add_on_limit,
                spec_details,
            };
        });
    };

    const isAllSpecsSelected = () => {
        if (!selectedProduct || selectedProduct.spec_details.length === 0) {
            return true;
        }

        return !!selectedSpecs && !!selectedSpecs.id;
    };

    const handleClose = () => {
        setVisible(false);
        setCartId(null);
        setSelectedProduct(null);
        setSelectedSpecs(null);
    };

    const handleSubmit = async () => {
        if (!selectedProduct) return;
        const { product_id, promo_id, spec_details } = selectedProduct;

        const specDetailId = spec_details.find(
            (spec) => spec.id === selectedSpecs?.id
        )?.id;

        try {
            setIsSubmitting(true);
            const resp = await calculate(async () => {
                return postCartOrderGiveaway({
                    product_id,
                    promo_id,
                    spec_detail_id: specDetailId,
                }).then((_resp) => {
                    if (_resp?.data) {
                        toast({
                            title: i18n.gettext('已加入購物車'),
                            status: 'success',
                        });
                    }

                    return _resp;
                });
            });

            if (resp?.data) {
                handleClose();

                logEvent(TRACK_EVENT.ADD_TO_CART, {
                    event: TRACK_EVENT.ADD_TO_CART,
                });
            }
        } catch (error) {
            console.error(error);
            handleClose();
            toast({
                title: i18n.gettext('商品庫存不足或其他錯誤，請再試一次！'),
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const Component = isMobile ? MobileBottomSheet : LaptopModal;

    return (
        <Component
            cartId={cartId}
            visible={visible}
            handleClose={handleClose}
            promoThreshold={promoThreshold}
            promoCopy={promoCopy}
            promos={promos}
            selectedProduct={selectedProduct}
            selectedSpecs={selectedSpecs}
            handleSelectProduct={handleSelectProduct}
            setSelectedSpecs={setSelectedSpecs}
            isAllSpecsSelected={isAllSpecsSelected}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
        />
    );
});

OrderGiveawaySheetModal.displayName = 'OrderGiveawaySheetModal';
